'use client';

import { Elements, ElementsConsumer, PaymentElement, LinkAuthenticationElement } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js';
import CardPaymentEmail from './CardPaymentEmail';
import CardPaymentButton from './CardPaymentButton';
import { useEffect, useMemo, useState } from 'react';
import ThirdPartyPaymentButton from './ThirdPartyPaymentButton';
import { Separator } from '@/components/atoms/Separator';
import { useStripeContext } from '@/context/StripeContextProvider';
import { track } from '@/utils/mixpanel';
import { useStripePaymentConfirmation } from './stripe';

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY);

export const updateCustomer = ({ type, customerId, email }) => {
    if (!customerId || !email) {
        return;
    }

    return fetch("/best/stripe-customer", {
        method: "POST",
        body: JSON.stringify({ customerId, email }),
    })
        .then(response => {
            if (!response.ok) {
                track(`stripe_${type}_customer_update_failed`, {
                    error: "failed_to_update_customer",
                    errorMessage: response.statusText,
                    email,
                    customerId,
                    payment_method: type
                });
                throw new Error(`Failed to update customer: ${response.status}`);
            }

            track(`stripe_${type}_customer_update_succeeded`, {
                email,
                customerId,
                payment_method: type
            });
            return response.json();
        })
        .catch(error => {
            console.error("Failed to update customer", error);
            track(`stripe_${type}_customer_update_failed`, {
                error: "failed_to_update_customer",
                errorMessage: error.message,
                email,
                customerId,
                payment_method: type
            });

            return null;
        });
};

const Variants = {
    april: {
        separatorColor: "gray-400",
        separatorText: "Or with card",
        paymentButtonColor: "porange",
    },
    new_precheckout: {
        separatorColor: "white",
        separatorText: "or with card",
        paymentButtonColor: "gold",
    },
    default: {
        separatorText: "Or",
        paymentButtonColor: "porange",
    },
}

const ElementsWrapper = ({
    stripe,
    elements,
    branchLink,
    userId,
    withTrial,
    isNewFlow,
    subId,
    cancelSubscriptionId,
    customerId,
    variant,
    setIsPristine,
    userEmail = "",
    onEmailChange,
    plan,
}) => {
    // const [email, setEmail] = useState(userEmail);
    const [paymentRequest, setPaymentRequest] = useState(null);
    const { confirmPayment, errorMessage } = useStripePaymentConfirmation({
        branchUrl: branchLink,
        userId,
        withTrial,
        isNewFlow,
        subscriptionId: subId,
        cancelSubscriptionId,
        customerId,
        stripe,
        elements,
    });

    console.log({ userEmail });

    return (
        <>
            <ThirdPartyPaymentButton
                plan={plan}
                setPaymentRequest={setPaymentRequest}
                confirmPayment={confirmPayment}
            />
            {paymentRequest
                ? <Separator
                    textColor={Variants[variant].separatorColor}
                    text={Variants[variant].separatorText} /> : null}
            {/* {userEmail
                ? null
                :
                <CardPaymentEmail
                    email={email}
                    updateEmail={setEmail} />
            } */}
            <LinkAuthenticationElement
                options={{
                    defaultValues: {
                        email: userEmail,
                    }
                }}
                onChange={(event) => {
                    console.log('LinkAuthenticationElement', event);
                    if (event.complete) {
                        onEmailChange(event.value.email);
                    }
                }} />
            <label className='text-sky-950 text-sm'>
                Payment method
            </label>
            <PaymentElement
                onChange={(event) => {
                    console.log(event);
                    setIsPristine(event.collapsed);
                }}
                options={{
                    layout: {
                        type: 'accordion',
                        defaultCollapsed: true,
                    }
                }} />
            <CardPaymentButton
                errorMessage={errorMessage}
                withTrial={withTrial}
                color={Variants[variant].paymentButtonColor}
                confirmPayment={() => confirmPayment(userEmail)}
            />
        </>
    )
};

export default function StripePaymentContainer({
    variant = "default",
    plan,
    userEmail: initialUserEmail,
    onEmailChange,
    clientSecret: initialClientSecret,
}) {
    const [isPristine, setIsPristine] = useState(true);
    const { stripeData } = useStripeContext();
    const {
        clientSecret,
        customerId,
        branchLink,
        userId,
        withTrial,
        isNewFlow,
        monthlySubscriptionId,
        annualSubscriptionId,
        subscriptionId,
        userEmail,
    } = stripeData;

    const { subId, cancelSubscriptionId } = useMemo(() => {
        if (!plan) {
            return {
                subId: subscriptionId,
                cancelSubscriptionId: null
            };
        }

        if (plan === 'month') {
            return {
                subId: monthlySubscriptionId,
                cancelSubscriptionId: annualSubscriptionId
            };
        }

        if (plan === 'year') {
            return {
                subId: annualSubscriptionId,
                cancelSubscriptionId: monthlySubscriptionId
            };
        }

        return {
            subId: subscriptionId,
            cancelSubscriptionId: null
        };
    }, [plan, monthlySubscriptionId, annualSubscriptionId, subscriptionId]);

    console.log({ plan, subscriptionId, subId, cancelSubscriptionId });

    return (
        <div className="mb-8" id="payment-section">
            <Elements stripe={stripePromise} options={{ clientSecret: initialClientSecret || clientSecret }}>
                <ElementsConsumer>
                    {({ stripe, elements }) => (
                        <ElementsWrapper
                            stripe={stripe}
                            elements={elements}
                            branchLink={branchLink}
                            userId={userId}
                            withTrial={withTrial}
                            isNewFlow={isNewFlow}
                            subId={subId}
                            cancelSubscriptionId={cancelSubscriptionId}
                            customerId={customerId}
                            variant={variant}
                            plan={plan}
                            setIsPristine={setIsPristine}
                            userEmail={initialUserEmail || userEmail}
                            onEmailChange={onEmailChange}
                        />
                    )}
                </ElementsConsumer>
            </Elements>
        </div>
    );
}